import { useEffect, useState, useCallback } from "react";
import { Modal, Spin, Table, Tooltip } from "antd";
import { Download, Eye } from "lucide-react";
import LoadingOverlay from "../shared/LoadingOverlay";
import Toast from "../shared/Toast";
import {
    fetchLatestApprovalCertificateList as fetchApprovalCertificateFromApi,
    downloadApprovalCertificate as downloadApprovalCertificateFromApi
} from "../../services/documentApi";
import { getAccessToken } from "../../services/authApi";
import { checkLoanResourceLock, fieldReader } from "../../services/loanApi";
import { generateAdditionalDocuments } from "./approvalCertificateHelper";
import { hasPageAccess } from "../../services/personaApi";
import { error as logAppInsightError } from "../../utils/logger";
import { logError } from "../../utils/logError";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/legacy/build/pdf.worker.min.mjs";
pdfjsLib.GlobalWorkerOptions.workerSrc = "";

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

const SHOW_NOTIFY_BROKER_BUTTON =
    String(process.env.REACT_APP_SHOW_NOTIFY_BROKER_BUTTON).toLowerCase() === "true";

export default function ApprovalCertificatePage() {
    const [loanId, setLoanId] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("success");

    const [attachmentsLoading, setAttachmentsLoading] = useState(false);
    const [approvalAttachments, setApprovalAttachments] = useState([]); // array from backend
    const [currentMilestone, setCurrentMilestone] = useState("");
    const [milestones, setMilestones] = useState([]);

    const allowedMilestones = [
        "Submitted to UW",
        "Initial Approval",
        "Submit for Condition Review",
        "Conditional Approval",
        "Submit for Final Review",
        "CTC"
    ];

    const isMilestoneDone = (milestoneName) => {
        if (!milestoneName) return false;

        const normalizedCurrent = milestoneName.toLowerCase();

        return (
            allowedMilestones.some(
                name => name.toLowerCase() === normalizedCurrent
            ) &&
            milestones?.some(
                m =>
                    m.name?.toLowerCase() === normalizedCurrent &&
                    m.doneIndicator === true
            )
        );
    };

    const canGenerateConditional = isMilestoneDone(currentMilestone);
    const canGenerateFinal = isMilestoneDone(currentMilestone);

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewTitle, setPreviewTitle] = useState("");
    const [pdfPages, setPdfPages] = useState([]);
    const [loanLocked, setLoanLocked] = useState(false);
    const [lockedByUser, setLockedByUser] = useState("");
    const [loanType, setLoanType] = useState("");

    const [canGenerate, setCanGenerate] = useState(true); // like canSave
    const [permissionMessage, setPermissionMessage] = useState("");

    useEffect(() => {
        const boot = async () => {
            // 1) loan
            try {
                if (window.elli?.script?.guest) {
                    window.elli.script.guest.create();

                    window.elli.script.getObject("loan").then(async function (loanObject) {
                        const loan = await loanObject.all();

                        setMilestones(loan?.loan?.milestones);
                        setLoanId(loan?.loan?.id || "");

                        const authObj = await window.elli.script.getObject("auth");
                        const authCode = await authObj.createAuthCode();
                        const token = await getAccessToken(authCode);

                        const fields = ["Log.MS.CurrentMilestone", "CX.LOAN.TYPE", "CX.LOAN.PROD.BROKER.SELECT", "CX.DOC.TYPE"];

                        const data = await fieldReader(token, fields, loan?.loan?.id);

                        setCurrentMilestone(data["Log.MS.CurrentMilestone"]);
                        setLoanType(data["CX.LOAN.TYPE"] || "");

                    });


                } else {
                    console.warn("elli.script not available");
                }
            } catch (err) {
                console.error("Failed to read loan id from elli script", err);
            }

            // 2) auth + persona gating
            try {
                const authObj = await window.elli.script.getObject("auth");
                const user = await authObj.getUser();
                console.log("Current user:", user);
                const allowed = await hasPageAccess("ApprovalCertificatePage", user);
                setCanGenerate(allowed);

                if (!allowed) {
                    setPermissionMessage(
                        "Allow Generate only for Underwriting Persona; other personas will be view-only."
                    );
                } else {
                    setPermissionMessage("");
                }
            } catch (e) {
                console.warn("Failed to read auth user:", e);
                setCanGenerate(false);
                setPermissionMessage(
                    "Allow Generate only for Underwriting Persona; other personas will be view-only."
                );
            }
        };
        boot();
    }, []);


    // when loanId changes fetch attachments
    useEffect(() => {
        if (loanId) {
            fetchLatestApprovalCertificate();
            checkIfLoanLocked();
        }
    }, [loanId]);

    const showToast = (message, type = "success") => {
        setToastMessage(message);
        setToastType(type);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 5000);
    };

    // call the generate endpoint you had earlier
    const callGenerateApi = async (docType) => {
        if (!canGenerate) {
            showToast(permissionMessage || "You don’t have permission to generate.", "error");
            return;
        }

        if (loanLocked) {
            showToast(`This loan is locked by '${lockedByUser}'. You cannot generate.`, "error");
            return;
        }

        setLoading(true);
        setLoadingText(`Generating ${docType} approval certificate...`);
        try {
            if (!loanId) {
                showToast("Loan ID not found. Cannot generate approval certificate.", "error");
                return;
            }
            const authObj = await window.elli.script.getObject("auth");
            const authCode = await authObj.createAuthCode();
            const endpoint = `${API_DOMAIN}/api/ApprovalCertificate/v2/generate`;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    loanId,
                    authCode,
                    docType
                })
            });

            if (!response.ok) {
                const text = await response.text().catch(() => response.statusText || "Unknown error");
                console.error("Approval Certificate API error", response.status, text);
                showToast(`Failed to generate ${docType} certificate: ${response.status} - ${text}`, "error");
            } else {
                if (loanType.toUpperCase() === "CONSUMER") {
                    setLoadingText(
                        "Please wait while we generate " +
                        "1008 (Transmittal Summary) and 2020 URLA (1003/65) - All Pages"
                    );
                } else {
                    setLoadingText(
                        "Please wait while we generate " +
                        "1008 (Transmittal Summary) and Commercial Loan Application"
                    );
                }

                await generateAdditionalDocuments(loanId);

                showToast("Approval Certificate and supporting documents generated successfully.", "success");

                await fetchLatestApprovalCertificate();
            }
        } catch (err) {
            console.error("Error generating approval certificate", err);
            showToast(`Error: ${err?.message || err}`, "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    const notifyBroker = async (docType) => {
        if (!canGenerate) {
            showToast(permissionMessage || "You don’t have permission to notify broker.", "error");
            return;
        }

        if (loanLocked) {
            showToast(`This loan is locked by '${lockedByUser}'. You cannot notify broker.`, "error");
            return;
        }

        if (!loanId) {
            showToast("Loan ID not found. Cannot notify broker.", "error");
            return;
        }

        setLoading(true);
        setLoadingText(`Sending ${docType} approval letter email to broker...`);

        try {
            const authObj = await window.elli.script.getObject("auth");
            const authCode = await authObj.createAuthCode();

            const endpoint = `${API_DOMAIN}/api/ApprovalCertificate/notify-broker`;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    loanId,
                    authCode,
                    docType
                })
            });

            if (!response.ok) {
                const text = await response.text().catch(() => response.statusText || "Unknown error");
                showToast(`Failed to notify broker: ${response.status} - ${text}`, "error");
                return;
            }

            showToast("Broker notification email sent successfully.", "success");
        } catch (err) {
            console.error("Notify broker error:", err);
            showToast(`Error: ${err?.message || err}`, "error");
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    // fetch attachments via the service wrapper
    const fetchLatestApprovalCertificate = useCallback(async () => {
        if (!loanId) return;
        setAttachmentsLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            // service call (uses axios under the hood)
            const result = await fetchApprovalCertificateFromApi(authCode, loanId);

            if (Array.isArray(result)) {
                setApprovalAttachments(result);
            } else {
                setApprovalAttachments(result ? [result] : []);
            }

        } catch (err) {
            const errorCode = logError("FETCH_ATTACHMENT_ERROR");
            logAppInsightError("Error fetching Approval Certificate", {
                component: "ApprovalCertificatePage",
                problemId: errorCode,
                errorCode,
                rawError: err,
                errorMessage: `${errorCode} - Failed to get Approval Certificate.`
            });
            console.error("fetchLatestApprovalCertificate error:", err);
            showToast("Failed to fetch approval certificate attachments.", "error");
        } finally {
            setAttachmentsLoading(false);
        }
    }, [loanId]);

    // download via service wrapper
    const downloadApprovalCertificate = useCallback(
        async (attachmentId, filename, type) => {
            try {
                if (!loanId) {
                    showToast("Loan ID not found. Cannot download certificate.", "error");
                    return;
                }
                if (!attachmentId) {
                    showToast("No document found to download.", "error");
                    return;
                }
                const tpoAuthObject = await window.elli.script.getObject("auth");
                const authCode = await tpoAuthObject.createAuthCode();

                const blob = await downloadApprovalCertificateFromApi(authCode, loanId, attachmentId, type);
                // blob is returned from axios as response.data (blob)
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = filename || "document.pdf";
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (err) {
                const errorCode = logError("DOWNLOAD_ATTACHMENT_ERROR");
                logAppInsightError("Download Approval Certificate failed", {
                    component: "ApprovalCertificatePage",
                    problemId: errorCode,
                    errorCode,
                    rawError: err,
                    errorMessage: `${errorCode} - Attachment download failed.`
                });
                console.error("downloadApprovalCertificate error:", err);
                showToast("Failed to download certificate.", "error");
            }
        },
        [loanId]
    );

    // table columns (equal widths for main three)
    const columns = [
        {
            title: "Document",
            dataIndex: "document",
            key: "document",
            width: "33%"
        },
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: "33%"
        },
        {
            title: "Created Date (Pacific)",
            dataIndex: "createdDatePacific",
            key: "createdDatePacific",
            width: "34%",
            render: (val) => (val ? val : "")
        }
    ];

    const findAttachment = (matchTitlePart) =>
        approvalAttachments.find((a) => (a.title || "").toLowerCase().includes(matchTitlePart.toLowerCase()));

    const conditionalAttachment = findAttachment("conditional");
    const finalAttachment = findAttachment("final");

    const canNotifyConditional =
        canGenerate &&
        canGenerateConditional &&
        !loading &&
        !loanLocked &&
        !!conditionalAttachment?.id;

    const canNotifyFinal =
        canGenerate &&
        canGenerateFinal &&
        !loading &&
        !loanLocked &&
        !!finalAttachment?.id;

    const buildRowFromAttachment = (att, fallbackKey) => {
        if (!att) {
            return {
                key: fallbackKey,
                document: "",
                title: "No document found",
                createdDatePacific: ""
            };
        }

        const createdDatePacific =
            att.createdDatePacific ||
            (att.createdDateUtc ? new Date(att.createdDateUtc).toLocaleString("en-US", { timeZone: "America/Los_Angeles" }) : "-");

        return {
            key: att.id,
            document: att.document || att.assignedToEntityType || att.assignedTo?.entityType || att.assignedTo?.entityName || "-",
            title: att.title || "-",
            createdDatePacific
        };
    };

    const conditionalRow = buildRowFromAttachment(conditionalAttachment, "conditional-none");
    const finalRow = buildRowFromAttachment(finalAttachment, "final-none");


    const renderPDF = async (blobUrl) => {
        const loadingTask = pdfjsLib.getDocument({
            url: blobUrl,
            disableWorker: true
        });

        const pdf = await loadingTask.promise;

        const pageList = [];
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 1.2 });

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = viewport.width;
            canvas.height = viewport.height;

            await page.render({ canvasContext: ctx, viewport }).promise;

            pageList.push(canvas.toDataURL());
        }

        return pageList;
    };

    const previewCertificate = async (attachmentId, type) => {
        try {
            setPreviewLoading(true);
            setPreviewVisible(true);
            setPdfPages([]);

            setPreviewTitle(
                type === "conditional"
                    ? "Conditional Approval Certificate"
                    : "Final Approval Certificate"
            );

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const blob = await downloadApprovalCertificateFromApi(
                authCode, loanId, attachmentId, type
            );

            const blobUrl = URL.createObjectURL(blob);

            const pages = await renderPDF(blobUrl);
            setPdfPages(pages);

            setPreviewVisible(true);
        } catch (err) {
            console.error("Preview error:", err);
            showToast("Unable to preview document.", "error");
        } finally {
            setPreviewLoading(false);
        }
    };

    const previewGeneratedPdf = async (docType) => {
        try {
            setPreviewLoading(true);
            setPreviewVisible(true);
            setPdfPages([]);

            setPreviewTitle(
                docType === "conditional"
                    ? "Conditional Approval Certificate (Preview Only)"
                    : "Final Approval Certificate (Preview Only)"
            );

            if (!loanId) {
                showToast("Loan ID not found. Cannot preview certificate.", "error");
                return;
            }

            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const endpoint = `${API_DOMAIN}/api/ApprovalCertificate/v2/preview-pdf-only`;

            const response = await fetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ loanId, authCode, docType })
            });

            if (!response.ok) {
                const errText = await response.text();
                showToast(`Failed to preview PDF: ${errText}`, "error");
                return;
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            // Use your existing renderPDF() method
            const pages = await renderPDF(blobUrl);
            setPdfPages(pages);

        } catch (err) {
            console.error("Preview-only error:", err);
            showToast("Unable to preview PDF.", "error");
        } finally {
            setPreviewLoading(false);
        }
    };

    const checkIfLoanLocked = async () => {
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();

            const token = await getAccessToken(authCode);

            const result = await checkLoanResourceLock(token, loanId);

            if (result.locked) {
                setLoanLocked(true);
                setLockedByUser(result.userId || result.fullName || "Another user");
            } else {
                setLoanLocked(false);
                setLockedByUser("");
            }
        } catch (err) {
            console.error("Lock check error:", err);
        }
    };

    return (
        <div className="space-y-10 p-25px">
            <div className="bg-[var(--white)] rounded-lg p-3 space-y-6 max-w-8xl mx-auto">

                <LoadingOverlay loading={loading} text={loadingText} />

                <div className="mt-6">
                    {attachmentsLoading ? (
                        <div className="flex justify-center items-center h-40">
                            <Spin size="large" />
                        </div>
                    ) : (
                        <>
                            {loanLocked && (
                                <div className="lock-banner">
                                    <div className="lock-message">

                                        {/* ICON */}
                                        <div className="lock-icon-wrapper">
                                            <span className="lock-icon">!</span>
                                        </div>

                                        <span>
                                            This loan is currently locked by '{lockedByUser}', so you cannot generate approval certificates.
                                        </span>
                                    </div>

                                    <div className="lock-close" onClick={() => setLoanLocked(false)}>
                                        ×
                                    </div>
                                </div>
                            )}


                            <div className="space-y-6">
                                {/* Conditional Panel */}
                                <div className="border rounded-lg shadow-sm bg-white">
                                    <div className="flex items-center justify-between px-4 py-3 border-b">
                                        <div className="text-lg font-medium text-gray-800">Conditional Approval Certificate</div>

                                        <div className="flex items-center gap-3">

                                            {/* PREVIEW ONLY BUTTON */}
                                            <Tooltip title="Preview only">
                                                <button
                                                    className={`btn-preview
                                                        ${canGenerateConditional
                                                            ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                            : "btn-custom-disabled"}`}
                                                    onClick={() => previewGeneratedPdf("conditional")}
                                                    disabled={!canGenerate || !canGenerateConditional || loading}
                                                >
                                                    Preview
                                                </button>
                                            </Tooltip>

                                            {/* GENERATE BUTTON */}
                                            <Tooltip title="Generate Certificate">
                                                <button
                                                    className={`btn-custom rounded px-4 py-2 font-semibold
                                                ${canGenerateConditional
                                                            ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                            : "btn-custom-disabled"}`}
                                                    onClick={() => callGenerateApi("conditional")}
                                                    disabled={!canGenerate || !canGenerateConditional || loading || loanLocked}
                                                >
                                                    Generate
                                                </button>
                                            </Tooltip>

                                            {/* NOTIFY BUTTON */}
                                            {SHOW_NOTIFY_BROKER_BUTTON && (
                                                <Tooltip title="Notify Broker">
                                                    <button
                                                        className={`btn-custom rounded px-4 py-2 font-semibold
                                                    ${canGenerateConditional
                                                                ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                                : "btn-custom-disabled"}`}
                                                        onClick={() => notifyBroker("conditional")}
                                                         disabled={!canNotifyConditional}
                                                    >
                                                        Notify Broker
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        {!canGenerateConditional && (!conditionalAttachment || !conditionalAttachment.id) ? (
                                            <div className="py-8 px-4 text-center text-gray-600">
                                                <div className="text-base font-medium mb-2">
                                                    Generate Conditional Approval Certificate button will be enabled only when the current milestone is{" "}
                                                    {allowedMilestones.join(", ")} and the milestone is marked as completed.
                                                </div>
                                            </div>
                                        ) : (
                                            (!conditionalAttachment || !conditionalAttachment.id) ? (
                                                <div className="py-8 px-4 text-center text-gray-600">
                                                    <div className="text-base font-medium mb-2">
                                                        Conditional approval certificate has not been generated.
                                                    </div>
                                                </div>
                                            ) : (
                                                <Table
                                                    size="small"
                                                    columns={[
                                                        ...columns,
                                                        {
                                                            title: "Actions",
                                                            key: "actions",
                                                            width: "10%",
                                                            render: () => (
                                                                <div className="flex items-center gap-4">
                                                                    {/* Preview */}
                                                                    <Tooltip title="Preview PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                previewCertificate(
                                                                                    conditionalAttachment.id,
                                                                                    "conditional"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Eye size={18} />
                                                                        </button>
                                                                    </Tooltip>

                                                                    {/* Download */}
                                                                    <Tooltip title="Download PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                downloadApprovalCertificate(
                                                                                    conditionalAttachment.id,
                                                                                    "Conditional_Approval_Certificate.pdf",
                                                                                    "conditional"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Download size={18} />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            )
                                                        }

                                                    ]}
                                                    dataSource={[conditionalRow]}
                                                    pagination={false}
                                                    bordered
                                                />
                                            )
                                        )}

                                    </div>
                                </div>

                                {/* Final Panel */}
                                <div className="border rounded-lg shadow-sm bg-white">
                                    <div className="flex items-center justify-between px-4 py-3 border-b">
                                        <div className="text-lg font-medium text-gray-800">Final Approval Certificate</div>

                                        <div className="flex items-center gap-3">

                                            {/* PREVIEW ONLY BUTTON */}
                                            <Tooltip title="Preview only">
                                                <button
                                                    className={`btn-preview
                                                        ${canGenerateFinal
                                                            ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                            : "btn-custom-disabled"}`}
                                                    onClick={() => previewGeneratedPdf("final")}
                                                    disabled={!canGenerate || !canGenerateFinal || loading}
                                                >
                                                    Preview
                                                </button>
                                            </Tooltip>

                                            {/* GENERATE BUTTON */}
                                            <Tooltip title="Generate Certificate">
                                                <button
                                                    className={`btn-custom rounded px-4 py-2 font-semibold
                                                    ${canGenerateFinal
                                                            ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                            : "btn-custom-disabled"}`}
                                                    onClick={() => callGenerateApi("final")}
                                                    disabled={!canGenerate || !canGenerateFinal || loading || loanLocked}
                                                >
                                                    Generate
                                                </button>
                                            </Tooltip>

                                            {/* NOTIFY BUTTON */}
                                            {SHOW_NOTIFY_BROKER_BUTTON && (
                                                <Tooltip title="Notify Broker">
                                                    <button
                                                        className={`btn-custom rounded px-4 py-2 font-semibold
                                                        ${canGenerateFinal
                                                                ? "text-white bg-[#2080C3] hover:bg-[#5DA5D9]"
                                                                : "btn-custom-disabled"}`}
                                                        onClick={() => notifyBroker("final")}
                                                        disabled={!canNotifyFinal}
                                                    >
                                                        Notify Broker
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                    <div className="p-4">
                                        {!canGenerateFinal && (!finalAttachment || !finalAttachment.id) ? (
                                            <div className="py-8 px-4 text-center text-gray-600">
                                                <div className="text-base font-medium mb-2">
                                                    Generate Final Approval Certificate button will be enabled only when the current milestone is{" "}
                                                    {allowedMilestones.join(", ")} and the milestone is marked as completed.
                                                </div>
                                            </div>
                                        ) : (
                                            (!finalAttachment || !finalAttachment.id) ? (
                                                <div className="py-8 px-4 text-center text-gray-600">
                                                    <div className="text-base font-medium mb-2">
                                                        Final approval certificate has not been generated.
                                                    </div>
                                                </div>
                                            ) : (
                                                <Table
                                                    size="small"
                                                    columns={[
                                                        ...columns,
                                                        {
                                                            title: "Actions",
                                                            key: "actions",
                                                            width: "10%",
                                                            render: () => (
                                                                <div className="flex items-center gap-4">
                                                                    {/* Preview */}
                                                                    <Tooltip title="Preview PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                previewCertificate(
                                                                                    finalAttachment.id,
                                                                                    "final"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Eye size={18} />
                                                                        </button>
                                                                    </Tooltip>

                                                                    {/* Download */}
                                                                    <Tooltip title="Download PDF">
                                                                        <button
                                                                            className="hover:opacity-80"
                                                                            onClick={() =>
                                                                                downloadApprovalCertificate(
                                                                                    finalAttachment.id,
                                                                                    "Final_Approval_Certificate.pdf",
                                                                                    "final"
                                                                                )
                                                                            }
                                                                        >
                                                                            <Download size={18} />
                                                                        </button>
                                                                    </Tooltip>
                                                                </div>
                                                            )
                                                        }

                                                    ]}
                                                    dataSource={[finalRow]}
                                                    pagination={false}
                                                    bordered
                                                />
                                            )
                                        )}

                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <Toast visible={toastVisible} type={toastType} message={toastMessage} onClose={() => setToastVisible(false)} />
            </div>
            <Modal
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width="70vw"
                centered
                styles={{
                    body: {
                        padding: 0,
                        backgroundColor: "#f5f7fa",
                        borderRadius: "8px",
                        overflow: "hidden",
                        height: "85vh",
                        overflowY: "auto"
                    },
                    header: {
                        fontSize: "18px",
                        fontWeight: 600,
                        padding: "16px 24px",
                        background: "#ffffff",
                        borderBottom: "1px solid #e5e7eb"
                    },
                    content: {
                        borderRadius: "10px",
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
                    }
                }}
                title={previewTitle}
            >
                {previewLoading ? (
                    <div className="flex justify-center items-center h-full">
                        <Spin size="large" />
                    </div>
                ) : (
                    <div className="pdf-body-container">
                        {pdfPages.map((src, index) => (
                            <img key={index} src={src} alt={`Page ${index + 1}`} className="pdf-page" />
                        ))}
                    </div>
                )}
            </Modal>
        </div>
    );
}
